import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getBlogPage, BLOG_PAGES } from '../lib/blog-pages';
import { PROFESSION_CONTENT, CITY_CONTENT, SAC_CONTENT } from '../lib/blog-content';
import Generator from './Generator';
import { getStateByShort } from '../lib/states';

function FAQStructuredData({ page }) {
  const questions = [
    { q: 'Is this GST invoice generator free?', a: 'Yes. You can download one-off invoices without login. Free accounts include 5 watermarked invoices a month with saved history.' },
    { q: 'Is the invoice GST-compliant?', a: 'Yes. It automatically calculates CGST + SGST for intra-state transactions and IGST for inter-state, includes GSTIN, HSN/SAC codes, and amount in words — all mandatory fields.' },
    { q: 'Can I use this without signing up?', a: 'Yes. billjaldi runs entirely in your browser. No account, no email, nothing sent to any server.' },
  ];
  const json = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((qa) => ({
      '@type': 'Question',
      name: qa.q,
      acceptedAnswer: { '@type': 'Answer', text: qa.a },
    })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}

function ProfessionContent({ page }) {
  const c = PROFESSION_CONTENT[page.profession];
  if (!c) return null;
  return (
    <div className="prose prose-slate max-w-none text-base leading-relaxed">
      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">
        GST for {page.profession}: SAC {c.sacCode} at {c.gstRate}
      </h2>
      {c.body.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-6">
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-orange-600 mb-1">Pro tip</div>
        <p className="text-sm text-slate-700 m-0">{c.tip}</p>
      </div>

      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">What this generator handles for you</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>CGST / SGST / IGST auto-split</strong> — detects intra-state vs inter-state from your selected states.</li>
        <li><strong>GSTIN validation</strong> with checksum — catches typos before the invoice goes out.</li>
        <li><strong>Multiple line items</strong> — each with its own HSN/SAC code and GST rate on the same invoice.</li>
        <li><strong>Amount in words</strong> in Indian numbering (lakhs, crores) — required on every tax invoice.</li>
        <li><strong>Clean A4 PDF</strong> — print-ready, professional, instantly downloadable.</li>
      </ul>

      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">How to create your invoice</h2>
      <ol className="list-decimal pl-6 space-y-1">
        <li>Enter your name/firm, GSTIN, and state below.</li>
        <li>Enter your client's name, GSTIN (if registered), and their state — tax type sets automatically.</li>
        <li>Add line items: description, SAC <strong>{c.sacCode}</strong>, quantity, rate, and GST rate.</li>
        <li>Add your bank account and UPI ID for easy payment.</li>
        <li>Download PDF. Done in under 30 seconds.</li>
      </ol>
    </div>
  );
}

function CityContent({ page }) {
  const c = CITY_CONTENT[page.city];
  if (!c) return null;
  return (
    <div className="prose prose-slate max-w-none text-base leading-relaxed">
      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">
        GST invoicing in {page.city} — State code {c.stateCode} ({page.state})
      </h2>
      {c.body.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-6">
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-orange-600 mb-1">Local tip</div>
        <p className="text-sm text-slate-700 m-0">{c.tip}</p>
      </div>

      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">How intra-state vs inter-state works</h2>
      <p>When you (supplier) and your client (recipient) are both in <strong>{page.state}</strong>, your invoice shows:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>CGST {' '}(Central GST) — half the applicable rate</li>
        <li>SGST (State GST) — the other half</li>
      </ul>
      <p>When your client is in any other state, the invoice shows a single IGST line at the full rate. billjaldi switches between the two automatically when you select states — no manual calculation needed.</p>

      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">How to create your {page.city} GST invoice</h2>
      <ol className="list-decimal pl-6 space-y-1">
        <li>Select <strong>{page.state}</strong> as your state below — the form pre-fills state code {c.stateCode}.</li>
        <li>Enter your GSTIN (starts with {c.stateCode}).</li>
        <li>Enter your client's state — tax type calculates automatically.</li>
        <li>Add line items with the correct HSN/SAC codes and rates.</li>
        <li>Download PDF. Done in 30 seconds.</li>
      </ol>
    </div>
  );
}

function SACContent({ page }) {
  const c = SAC_CONTENT[page.sac];
  if (!c) return null;
  return (
    <div className="prose prose-slate max-w-none text-base leading-relaxed">
      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">
        What is SAC {page.sac}?
      </h2>
      <p><strong>SAC {page.sac} — {c.fullName}</strong> — is taxed at <strong>{c.gstRate} GST</strong>.</p>
      <p><strong>Who uses this code:</strong> {c.who}</p>

      {c.body.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-6">
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-orange-600 mb-1">SAC tip</div>
        <p className="text-sm text-slate-700 m-0">{c.tip}</p>
      </div>

      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">How to add SAC {page.sac} to your invoice</h2>
      <ol className="list-decimal pl-6 space-y-1">
        <li>In the line items section below, click "Add row".</li>
        <li>Enter your service description.</li>
        <li>In the HSN/SAC column, enter <strong>{page.sac}</strong>.</li>
        <li>Set the GST rate to <strong>{c.gstRate}</strong>.</li>
        <li>Enter quantity and rate — the tax amount calculates automatically.</li>
        <li>Download PDF.</li>
      </ol>
    </div>
  );
}

function GenericContent() {
  return (
    <div className="prose prose-slate max-w-none text-base leading-relaxed">
      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">Why a GST-compliant invoice matters</h2>
      <p>Indian law requires registered businesses to issue invoices with GSTIN, HSN/SAC codes, and the correct split of CGST/SGST or IGST. Missing these details can delay payments, create compliance risk, and block input tax credit claims for your clients.</p>

      <h2 className="font-heading text-2xl font-bold mt-8 mb-3">What this generator handles for you</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>GSTIN validation</strong> with checksum — catches typos before your invoice goes out.</li>
        <li><strong>State-based tax logic</strong> — auto-detects CGST+SGST vs IGST.</li>
        <li><strong>HSN/SAC support</strong> — per line item, at 0%, 5%, 12%, 18%, or 28%.</li>
        <li><strong>Amount in words</strong> in Indian numbering (lakhs, crores).</li>
        <li><strong>Clean PDF output</strong> — A4, print-ready.</li>
      </ul>
    </div>
  );
}

function getRelated(page) {
  const slug = page.slug;
  const cat = page.category;

  if (cat === 'profession') {
    // Same category (other professions) + 2 city pages
    const sameCat = BLOG_PAGES.filter((p) => p.slug !== slug && p.category === 'profession').slice(0, 4);
    const cities = BLOG_PAGES.filter((p) => p.category === 'city').slice(0, 2);
    return [...sameCat, ...cities];
  }
  if (cat === 'city') {
    // Other cities (3) + relevant profession pages (3)
    const otherCities = BLOG_PAGES.filter((p) => p.slug !== slug && p.category === 'city').slice(0, 3);
    const professions = BLOG_PAGES.filter((p) => p.category === 'profession').slice(0, 3);
    return [...otherCities, ...professions];
  }
  if (cat === 'sac') {
    // Professions that use this SAC + other SAC pages
    const matchProfessions = BLOG_PAGES.filter((p) => p.category === 'profession' && p.sacHint === page.sac).slice(0, 4);
    const otherSac = BLOG_PAGES.filter((p) => p.slug !== slug && p.category === 'sac');
    return [...matchProfessions, ...otherSac];
  }
  return BLOG_PAGES.filter((p) => p.slug !== slug).slice(0, 6);
}

function relatedTitle(r) {
  if (r.title) return r.title;
  if (r.profession) return `GST invoice for ${r.profession}`;
  if (r.city) return `GST invoice generator for ${r.city}`;
  return `SAC ${r.sac} invoice format`;
}

export default function BlogPost() {
  const { slug } = useParams();
  const page = getBlogPage(slug);

  const title = page ? (page.title || (page.profession ? `GST Invoice Format for ${page.profession} in India` : page.city ? `Free GST Invoice Generator for ${page.city}` : `SAC ${page.sac} Invoice Format`)) : '';
  const stateObj = page?.stateShort ? getStateByShort(page.stateShort) : null;
  const presetCustomer = stateObj ? { state: stateObj.short, stateName: stateObj.name } : null;

  React.useEffect(() => {
    if (title) document.title = `${title} | billjaldi`;
  }, [title]);

  if (!page) return <Navigate to="/" replace />;

  const related = getRelated(page);

  return (
    <div data-testid="blog-post-page">
      <FAQStructuredData page={page} />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-6">
          <Link to="/" className="hover:text-orange-600">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-orange-600">Guides</Link>
          <span>/</span>
          <span className="text-slate-600">{page.category === 'profession' ? page.profession : page.category === 'city' ? page.city : `SAC ${page.sac}`}</span>
        </div>

        <div className="mb-3 text-[11px] tracking-[0.2em] uppercase font-bold text-orange-600">
          {page.category === 'profession' ? 'For ' + page.profession : page.category === 'city' ? page.city + ', India' : 'SAC Code ' + page.sac}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black text-slate-900 tracking-tight mb-4">{title}</h1>
        <p className="text-lg text-slate-600 leading-relaxed mb-6">{page.intro}</p>

        {page.category === 'profession' && <ProfessionContent page={page} />}
        {page.category === 'city' && <CityContent page={page} />}
        {page.category === 'sac' && <SACContent page={page} />}
        {!['profession', 'city', 'sac'].includes(page.category) && <GenericContent />}
      </article>

      {/* Embedded generator */}
      <div id="generator" className="border-t border-slate-200 bg-slate-50">
        <Generator presetCustomer={presetCustomer} />
      </div>

      {/* FAQ + related links */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-heading text-2xl font-bold mb-4">Frequently asked</h2>
        <dl className="space-y-5">
          <div>
            <dt className="font-bold text-slate-900">Is billjaldi free?</dt>
            <dd className="text-slate-600 mt-1">Yes. You can download one-off invoices without login. Free accounts include 5 watermarked invoices a month with saved history.</dd>
          </div>
          <div>
            <dt className="font-bold text-slate-900">Is the PDF GST-compliant?</dt>
            <dd className="text-slate-600 mt-1">Yes. It includes GSTIN, HSN/SAC codes, correct CGST/SGST/IGST split, and amount in words — all mandatory fields for Indian tax invoices.</dd>
          </div>
          <div>
            <dt className="font-bold text-slate-900">Do I need to create an account?</dt>
            <dd className="text-slate-600 mt-1">No account needed. billjaldi runs entirely in your browser — nothing is sent to any server.</dd>
          </div>
          {page.category === 'profession' && (
            <div>
              <dt className="font-bold text-slate-900">What SAC code do {page.profession} use?</dt>
              <dd className="text-slate-600 mt-1">
                {PROFESSION_CONTENT[page.profession]
                  ? `${page.profession} typically use SAC ${PROFESSION_CONTENT[page.profession].sacCode} at ${PROFESSION_CONTENT[page.profession].gstRate} GST. You can enter this directly in the HSN/SAC field for each line item.`
                  : `Check the line item HSN/SAC field in the generator below — enter the code that matches your specific service type.`}
              </dd>
            </div>
          )}
          {page.category === 'city' && (
            <div>
              <dt className="font-bold text-slate-900">What is the GST state code for {page.city}?</dt>
              <dd className="text-slate-600 mt-1">
                {CITY_CONTENT[page.city]
                  ? `${page.city} is in ${page.state}, which has GST state code ${CITY_CONTENT[page.city].stateCode}. This pre-fills automatically when you select ${page.state} in the generator.`
                  : `Select ${page.state} in the state dropdown — the state code pre-fills automatically.`}
              </dd>
            </div>
          )}
        </dl>

        <h2 className="font-heading text-2xl font-bold mt-12 mb-4">Related guides</h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {related.map((r) => (
            <li key={r.slug}>
              <Link to={`/blog/${r.slug}`} className="text-sm text-slate-600 hover:text-orange-600 hover:underline">
                {relatedTitle(r)}
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-10 pt-8 border-t border-slate-200 text-center">
          <Link to="/blog" className="text-sm font-semibold text-slate-500 hover:text-orange-600">
            ← All GST invoice guides
          </Link>
        </div>
      </div>
    </div>
  );
}
